import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiError, MACHINE_TYPES, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHead } from "@/pages/AppLayout";
import { toast } from "sonner";
import PlateScanner from "@/components/PlateScanner";
import { ArrowLeft, AlertTriangle, ImagePlus, X } from "lucide-react";

const EMPTY = { type: "tractor", make: "", model: "", fleet_number: "", serial: "", year: "", running_hours: "", odometer_km: "", notes: "", rego_number: "", rego_renewal_date: "", monthly_budget: "" };
const MAX_PHOTOS = 8;

const MAKES = [
  "John Deere", "Case IH", "New Holland", "Massey Ferguson", "Kubota", "Fendt",
  "Claas", "Deutz-Fahr", "Valtra", "McCormick", "Same", "Landini", "Iseki",
  "Caterpillar (CAT)", "Komatsu", "Volvo", "Hitachi", "JCB", "Bobcat", "Case",
  "Hyundai", "Doosan", "Liebherr", "Kobelco", "Takeuchi", "Yanmar", "Wacker Neuson",
  "Toyota", "Isuzu", "Ford", "Holden", "Hino", "Mitsubishi Fuso", "Mercedes-Benz",
  "Nissan", "Mahindra", "Kioti", "Husqvarna", "Stihl", "Toro", "Honda", "Polaris",
  "Can-Am", "Kawasaki", "Manitou", "Merlo", "Grimme", "Krone", "Kuhn", "Vaderstad",
  "Trimax", "Victa", "Rover", "Masport", "Cox", "Greenfield", "Hustler", "Ferris",
  "Scag", "Walker", "Cub Cadet", "Ariens", "Hayter", "Gianni Ferrari", "Shindaiwa",
  "Echo", "Makita", "Ryobi", "Baumr-AG", "Ozito", "Kanga", "Dingo", "Vermeer",
  "Ditch Witch", "Hardi", "Silvan", "Goldacres", "Croplands", "Gason", "Howard",
  "Briggs & Stratton", "Vanguard", "Kohler", "Rain Bird", "Toro Irrigation",
];

function resizeImage(file, maxSize = 1600, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e) => { img.src = e.target.result; };
    reader.onerror = reject;
    img.onload = () => {
      let { width, height } = img;
      if (width > maxSize || height > maxSize) {
        if (width >= height) { height = Math.round((height * maxSize) / width); width = maxSize; }
        else { width = Math.round((width * maxSize) / height); height = maxSize; }
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("Compression failed")), "image/jpeg", quality);
    };
    img.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function MachineForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const { loadFleet, isFree } = useAuth();
  const editing = Boolean(id);
  const fileRef = useRef(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [newPhotos, setNewPhotos] = useState([]);       // [{blob, url}]
  const [existingIds, setExistingIds] = useState([]);   // photo_ids from server
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    if (editing) api.get(`/machines/${id}`).then(({ data }) => {
      const m = data.machine;
      setForm({
        type: (m.type || "tractor").toLowerCase(), make: m.make, model: m.model, fleet_number: m.fleet_number || "",
        serial: m.serial || "", year: m.year || "", running_hours: m.running_hours || "",
        odometer_km: m.odometer_km || "", notes: m.notes || "", rego_number: m.rego_number || "",
        rego_renewal_date: m.rego_renewal_date ? m.rego_renewal_date.slice(0, 10) : "",
        monthly_budget: m.monthly_budget ?? "",
      });
      setExistingIds(m.photo_ids || []);
    });
    else api.get("/machines/next-fleet-number").then(({ data }) => setForm((f) => ({ ...f, fleet_number: data.fleet_number }))).catch(() => {});
  }, [id, editing]);

  const totalPhotos = existingIds.length + newPhotos.length;

  const onPickPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    for (const file of files) {
      if (totalPhotos + newPhotos.length >= MAX_PHOTOS) { toast.error(`Maximum ${MAX_PHOTOS} photos.`); break; }
      if (!file.type.startsWith("image/")) { toast.error("Please choose image files only."); continue; }
      try {
        const blob = await resizeImage(file);
        setNewPhotos((prev) => (prev.length + existingIds.length >= MAX_PHOTOS ? prev : [...prev, { blob, url: URL.createObjectURL(blob) }]));
      } catch { toast.error("Could not process an image."); }
    }
  };

  const removeNewPhoto = (idx) => setNewPhotos((prev) => prev.filter((_, i) => i !== idx));

  const removeExistingPhoto = async (photoId) => {
    try { await api.delete(`/machines/${id}/photos/${photoId}`); setExistingIds((prev) => prev.filter((x) => x !== photoId)); toast.success("Photo removed"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const uploadNewPhotos = async (machineId) => {
    for (const p of newPhotos) {
      const fd = new FormData();
      fd.append("file", p.blob, "machine.jpg");
      await api.post(`/machines/${machineId}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = {
      ...form,
      year: form.year ? parseInt(form.year) : null,
      running_hours: form.running_hours ? parseFloat(form.running_hours) : 0,
      odometer_km: form.odometer_km ? parseFloat(form.odometer_km) : 0,
      monthly_budget: form.monthly_budget ? parseFloat(form.monthly_budget) : null,
      rego_renewal_date: form.rego_renewal_date ? new Date(form.rego_renewal_date).toISOString() : null,
    };
    try {
      let machineId = id;
      if (editing) { await api.put(`/machines/${id}`, payload); }
      else { const { data } = await api.post("/machines", payload); machineId = data.id; }
      if (newPhotos.length && !isFree) {
        try { await uploadNewPhotos(machineId); }
        catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Some photos failed to upload"); }
      }
      toast.success(editing ? "Machine updated" : "Machine added");
      await loadFleet();
      nav(`/machines/${machineId}`);
    } catch (err) {
      const status = err.response?.status;
      if (status === 402) { toast.error("Machine limit reached — upgrade your plan."); nav("/billing"); }
      else toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="p-5 md:p-10 pb-24 max-w-3xl" data-testid="machine-form-page">
      <Link to={editing ? `/machines/${id}` : "/machines"} className="flex items-center gap-2 text-[#94A3B8] font-body text-sm mb-4"><ArrowLeft className="w-4 h-4" /> Back</Link>
      <PageHead title={editing ? "Edit Machine" : "Add Machine"} sub="Specs, registration and running hours." />

      <form onSubmit={submit} className="card p-6 md:p-8 space-y-5" data-testid="machine-form">
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <label className="il-label">Type</label>
            <select className="il-input capitalize" value={form.type} onChange={set("type")} data-testid="machine-type">
              {MACHINE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div><label className="il-label">Fleet No. *</label><input className="il-input font-mono uppercase" value={form.fleet_number} onChange={set("fleet_number")} required data-testid="machine-fleet-number" placeholder="e.g. UNIT-12" /></div>
          <div><label className="il-label">Make *</label><input className="il-input" list="il-makes" value={form.make} onChange={set("make")} required data-testid="machine-make" placeholder="Start typing… e.g. John Deere" />
            <datalist id="il-makes">{MAKES.map((mk) => <option key={mk} value={mk} />)}</datalist>
          </div>
          <div><label className="il-label">Model *</label><input className="il-input" value={form.model} onChange={set("model")} required data-testid="machine-model" placeholder="6155M" /></div>
          <div><label className="il-label">Serial No.</label><input className="il-input" value={form.serial} onChange={set("serial")} data-testid="machine-serial" /></div>
          <div><label className="il-label">Year</label><input className="il-input font-mono" type="number" value={form.year} onChange={set("year")} data-testid="machine-year" placeholder="2021" /></div>
          <div><label className="il-label">Running Hours</label><input className="il-input font-mono" type="number" step="0.1" value={form.running_hours} onChange={set("running_hours")} data-testid="machine-hours" placeholder="1200" /></div>
          <div><label className="il-label">Odometer (km)</label><input className="il-input font-mono" type="number" step="1" value={form.odometer_km} onChange={set("odometer_km")} data-testid="machine-km" placeholder="85000" /></div>
          <div>
            <label className="il-label">Rego Number</label>
            <div className="flex gap-2">
              <input className="il-input font-mono uppercase flex-1" value={form.rego_number} onChange={set("rego_number")} data-testid="machine-rego" placeholder="ABC123" />
              <PlateScanner
                label="Scan"
                testid="machine-rego-scan"
                className="btn-ghost h-12 px-3 font-head flex items-center gap-1.5 shrink-0 disabled:opacity-60"
                onResult={(d) => { if (d.plate) { setForm((f) => ({ ...f, rego_number: d.plate })); toast.success(`Rego read: ${d.plate}`); } }}
              />
            </div>
          </div>
          <div><label className="il-label">Rego Renewal Date</label><input className="il-input font-mono" type="date" value={form.rego_renewal_date} onChange={set("rego_renewal_date")} data-testid="machine-rego-date" /></div>
          <div><label className="il-label">Monthly Budget (AUD)</label><input className="il-input font-mono" type="number" step="1" min="0" value={form.monthly_budget} onChange={set("monthly_budget")} data-testid="machine-budget" placeholder="e.g. 500" /><p className="font-mono text-[10px] text-[#94A3B8] mt-1">Flagged in Analytics when this month&apos;s spend goes over.</p></div>
        </div>

        {/* Photo gallery */}
        {!isFree ? (
          <div data-testid="photo-upload-block">
            <label className="il-label">Photos <span className="text-[#94A3B8] normal-case">({totalPhotos}/{MAX_PHOTOS})</span></label>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {editing && existingIds.map((pid, i) => (
                <div key={pid} className="relative aspect-square" data-testid={`existing-photo-${i}`}>
                  <img src={`${API}/public/machines/${id}/photo/${i}`} alt="Machine" className="w-full h-full object-cover border border-white/10" />
                  <button type="button" onClick={() => removeExistingPhoto(pid)} className="absolute top-1 right-1 bg-[#0D0E11]/80 border border-white/20 p-1 hover:bg-[#FF3B30]/80 transition-colors" data-testid={`remove-existing-photo-${i}`}><X className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              {newPhotos.map((p, i) => (
                <div key={i} className="relative aspect-square" data-testid={`new-photo-${i}`}>
                  <img src={p.url} alt="New" className="w-full h-full object-cover border border-white/10" />
                  <button type="button" onClick={() => removeNewPhoto(i)} className="absolute top-1 right-1 bg-[#0D0E11]/80 border border-white/20 p-1 hover:bg-[#FF3B30]/80 transition-colors" data-testid={`remove-new-photo-${i}`}><X className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              {totalPhotos < MAX_PHOTOS && (
                <button type="button" onClick={() => fileRef.current?.click()} className="aspect-square border-2 border-dashed border-white/15 flex flex-col items-center justify-center gap-1 text-[#94A3B8] hover:border-[#FFB800]/50 hover:text-[#FFB800] transition-colors" data-testid="photo-picker-btn">
                  <ImagePlus className="w-6 h-6" />
                  <span className="font-body text-[11px]">Add</span>
                </button>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" multiple onChange={onPickPhoto} className="hidden" data-testid="machine-photo-input" />
          </div>
        ) : (
          <div className="border border-white/10 bg-[#0D0E11] p-3 font-body text-sm text-[#94A3B8]" data-testid="photo-locked">Photo upload is a paid feature. <Link to="/billing" className="text-[#FFB800]">Upgrade →</Link></div>
        )}

        <div><label className="il-label">Notes</label><textarea className="il-input py-3" style={{ height: "auto", minHeight: "6rem" }} value={form.notes} onChange={set("notes")} data-testid="machine-notes" /></div>

        {form.rego_renewal_date && !isFree && (
          <div className="flex items-start gap-2 border border-[#FFB800]/40 bg-[#FFB800]/10 p-3 font-body text-sm text-[#FFB800]">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> We&apos;ll auto-create rego reminders at 45, 30 and 15 days before this date.
          </div>
        )}

        <button className="btn-signal w-full h-14 font-head text-lg" disabled={loading} data-testid="machine-submit">
          {loading ? "Saving…" : editing ? "Save Changes" : "Add Machine"}
        </button>
      </form>
    </div>
  );
}
